import { html, property, state } from 'lit-element';
import { nothing } from 'lit-html';
import { repeat } from 'lit-html/directives/repeat.js';
import { cache } from 'lit-html/directives/cache.js';
import { ifNotNull } from '../../utils/directives';
import {
  Keys,
  register,
  nextUniqueId,
  KatLitMobileElement,
} from '../../shared/base';
import { formInputMap } from '../../utils/form-input-map';
import { fitHandler } from '../dropdown/fit-handler';
import baseStyles from '../../shared/base/base.lit.scss';
import formItemStyles from '../../shared/base/form-item.base.lit.scss';
import { debounce } from '../../utils/debounce';
import { isMobile, mobileMediaQuery } from '../../utils/mobile-helpers';
import styles from './predictive-input.lit.scss';
import optionStyles from './option.lit.scss';

type OptionsFilter = (element: string, index: number, array: []) => boolean;
const VISIBLE_ITEM_COUNT_BUFFER = 10;
const OPTION_HEIGHT = 33;
/**
 * @component {kat-predictive-input} KatalPredictiveinput A predictive input allows users to enter text and surfaces options that match the text the user has input from a finite pre-defined list of options.
 * @guideline Do Stringify data objects when passing them to attribute data
 * @example Basic {"value": "Test", "label":"Predictive Input", "options":["Test","Test Name","Katal","CXD","Flo"] }
 * @example WithTooltip {"value": "Test", "label":"Predictive Input",
 * "options":["Test","Test Name","Katal","CXD","Flo"],
 * "tooltip-text": "Tooltip content"}
 * @event {{ value: string }} change This event fires when focus on input is gone AND value is different. Detail object contains the current input value.
 * @event {{ value: string }} blur This event fires whenever the inner-input element fires blur. Detail object contains the inner-input value BEFORE focus is gone.
 * @event mouseup This event fires whenever the inner-input element fires mouseup. Detail object is unavailable for this event.
 * @event input This event fires whenever the inner-input element fires input. Detail object contains current input value.
 * @event {{ value: string }} focus This event fires whenever the inner-input element fires focus. Detail object contains the current input value.
 * @event keydown This event fires whenever the inner-input element fires keydown. Detail object is unavailable for this event.
 * @event expand This event fires whenever the options are expanded.
 * @event collapse This event fires whenever the options are collapsed.
 * @event onLoadItems This event fires when the input field is first expanded, when the scroll reaches the end of the options-inner-container, and when the value changes. Detail object contains the event type, and its corresponding information. type: expanded: contains if predictive input is expanded or collapsed, type: value: contains the current value in the input field, type: scroll: nothing
 * @theme flo
 * @status Production
 * @a11y {keyboard}
 * @a11y {sr}
 * @a11y {contrast}
 */
@register('kat-predictive-input')
export class KatPredictiveInput extends KatLitMobileElement {
  _customOptionsFilter?: OptionsFilter;

  /** Specifies the max length of characters allowed in the input */
  @property()
  maxLength?: number;

  /** Virtualizes options that are not currently in view from the DOM to improve performance for large lists. */
  @property({ attribute: 'virtual-scroll' })
  virtualScroll?: boolean;

  /** Specifies that the element is read only */
  @property()
  readOnly?: boolean;

  /** Whether single selection shows the clear button. Defaults to `false`. */
  @property({ attribute: 'show-clear' })
  showClear?: boolean;

  /**
   * Specifies that the element should receive focus by default
   * @required false
   */
  @property()
  autofocus: boolean;

  /** Specifies that the element should receive focus by default */
  @property()
  required?: boolean;

  /**
   * Sets how the list will be filtered
   * @enum {value} contains Will filter if the result contains the criteria
   * @enum {value} startsWith Will filter if the result starts with the criteria
   */
  @property({ attribute: 'filter-type' })
  filterType = 'contains';

  /** Array of `Option` strings, e.g. ["test","value","testValue","icon"] */
  @property()
  options: string[] = [];

  @state()
  private visibleOptions: string[] = [];

  /** The text that will be displayed to the user as a placeholder. */
  @property()
  placeholder?: string;

  /**
   * The max height of the selection area.
   * This should be a css value with units, e.g. "8px".
   */
  @property({ attribute: 'max-height' })
  maxHeight?: string;

  /** The optional field label. */
  @property()
  label?: string;

  /**
   * Label read by assistive technology.
   */
  @property({ attribute: 'kat-aria-label' })
  katAriaLabel?: string;

  /** The optional field name, hidden from view. */
  @property()
  name?: string;

  /** Whether the component is currently showing its dropdown options. */
  @property()
  expanded?: boolean;

  /** The value of the selected option. */
  @property()
  value?: string;

  /**
   * The size of the selected option.
   * @enum {value} large Large select box - Default
   * @enum {value} small Small select box
   */
  @property()
  size?: 'large' | 'small' = 'large';

  /** Setting the select box to disabled will stop its interactivity. */
  @property()
  disabled?: boolean;

  /** Used to associate an external label to an input.  See the kat-label for attribute. */
  @property({ attribute: 'unique-id' })
  uniqueId: string = nextUniqueId();

  /** Optional pretext for the constraint label.  Used to provide addtional context to the constraint for the input */
  @property({ attribute: 'constraint-emphasis' })
  constraintEmphasis?: string;

  /** Provides users with more information about what they enter into the input */
  @property({ attribute: 'constraint-label' })
  constraintLabel?: string;

  /**
   * Setting the state of the input changes its look to give the user more information about what they have entered.  This value must be set for State Labels to show up.
   * @enum {value} error Lets the user know there is a problem with the value they have entered in the input
   */
  @property()
  state?: string;

  /** Optional pretext for the state label.  Used to provide additional context to the state for the input */
  @property({ attribute: 'state-emphasis' })
  stateEmphasis?: string;

  /** Provides users with more information about why the input is in the state its in */
  @property({ attribute: 'state-label' })
  stateLabel?: string;

  /**
   * Specifies the autocomplete behavior that a browser should attempt:
   * https://developer.mozilla.org/en-US/docs/Web/HTML/Attributes/autocomplete
   */
  @property()
  autocomplete?: string;

  /**
   * The text to be shown inside the tooltip placed next to the label text. The tooltip will only appear if this is
   * set.
   */
  @property({ attribute: 'tooltip-text' })
  tooltipText?: string;

  /** The position of the tooltip. Defaults to "top". */
  @property({ attribute: 'tooltip-position' })
  tooltipPosition?: string;

  /** The icon that triggers the tooltip next to the label. Defaults to "help_outline". */
  @property({ attribute: 'tooltip-trigger-icon' })
  tooltipTriggerIcon?: string;

  /**
   * Controls whether element may be checked for spelling errors. Defaults to false.
   * See https://developer.mozilla.org/en-US/docs/Web/HTML/Global_attributes/spellcheck#security_and_privacy_concerns before enabling.
   */
  @property({ attribute: 'kat-spellcheck' })
  katSpellcheck = false;

  /** Set this property if options are being fetched asynchronously. Set to true when fetching and false when complete. */
  @property({ attribute: 'is-loading' })
  isLoading?: boolean;

  connectedCallback(): void {
    super.connectedCallback();

    mobileMediaQuery.addEventListener('change', () => {
      this.requestUpdate();
    });
  }

  /**
   * A function used to filter dropdown options.
   * @callback OptionsFilter
   * @param {string} element The current element being processed in the array.
   * @param {number} index The index of the current element being processed in the array.
   * @param {string[]} options The options being filtered.
   * @returns {boolean} True to keep the option in the array, false to remove it.
   */

  /**
   * Adds a custom function for filtering options
   *
   * @param {OptionsFilter} filter The function called to filter the dropdown options.
   * @katalmethod
   */
  addCustomFilter(filter: OptionsFilter) {
    this._customOptionsFilter = filter;
  }

  /**
   * Remove custom options filter, falling back to filterType property.
   * @katalmethod
   */
  removeCustomFilter() {
    this._customOptionsFilter = undefined;
  }

  static get styles() {
    return [baseStyles, formItemStyles, optionStyles, styles];
  }

  private _onSearchClear(e) {
    e.preventDefault();
    this.value = '';
    this.toggleExpanded(false);
  }

  firstUpdated() {
    super.firstUpdated();
    this.addEventListener('focusout', e => {
      const container = this.shadowRoot.querySelector('.kat-select-container');
      const newFocus = e.relatedTarget;
      if (!this.disabled && !container.contains(newFocus)) {
        this.dispatchCustomEvent('blur', {}, false);
        this.toggleExpanded(false);
      }
    });
  }

  @formInputMap([
    {
      tag: 'input',
      name: (component: KatPredictiveInput) => component.name,
      isNeeded: (component: KatPredictiveInput) =>
        component.isFormInputNeeded(),
      setup: (component: KatPredictiveInput, input: HTMLInputElement) =>
        component.setupFormInput(input),
    },
  ])
  updated(changedProperties) {
    super.updated(changedProperties);

    if (changedProperties.has('expanded')) {
      this.dispatchCustomEvent('onLoadItems', {
        type: { expanded: this.expanded },
      });
      if (this.virtualScroll) {
        this.visibleOptions = [];
      }
      this.setOrUnsetOptionsContainerBottomOffset();

      // In mobile view, there could be two inputs (one outside the emulated modal, and one within it)
      // which are duplicates of one another. This retargets focus on the one within the modal.
      if (this.expanded && isMobile()) {
        const modalInput = this.shadowRoot.querySelector<HTMLElement>(
          '[part~=mobile-emulated-modal-container] input'
        );
        modalInput?.focus();
      }
    }
    if (changedProperties.has('options') && this.isLoading) {
      // if options are not fetched on initial render, set the offset bottom so the height of the predictive-input does not flow off of the page.
      this.setOrUnsetOptionsContainerBottomOffset();
    }
    if (changedProperties.has('value')) {
      if (this.virtualScroll) {
        const optionContainer =
          this.shadowRoot.querySelector<HTMLElement>('.select-options');
        const optionInnerContainer = optionContainer.querySelector(
          '.option-inner-container'
        );
        optionInnerContainer.scrollTop = 0;
        this.visibleOptions = [];
      }
      this.dispatchCustomEvent('onLoadItems', {
        type: { value: this.value },
      });
    }
  }

  isFormInputNeeded() {
    return !(this.disabled || !this.name);
  }

  setupFormInput(input: HTMLInputElement) {
    input.value = this.value;
    this.readOnly && input.setAttribute('readonly', 'readonly');
    this.required && input.setAttribute('required', 'required');
  }

  // Need to calculate the menu height BEFORE render in order to know how many options to render when virtual scroll is set
  calculateVisibleItemCount() {
    const viewportHeight = window.innerHeight;
    const inputBottom = this.getBoundingClientRect().bottom;

    const optionContainer =
      this.shadowRoot.querySelector<HTMLElement>('.select-options');
    const optionInnerContainer = optionContainer.querySelector(
      '.option-inner-container'
    );

    const availableHeight = Math.max(viewportHeight - inputBottom, 0);
    const maxVisibleOptions = Math.floor(
      (this.maxHeight ? optionInnerContainer.clientHeight : availableHeight) /
        OPTION_HEIGHT
    );
    return maxVisibleOptions + VISIBLE_ITEM_COUNT_BUFFER;
  }

  calculateFirstVisibleOptionIndex() {
    const optionContainer =
      this.shadowRoot.querySelector<HTMLElement>('.select-options');
    const optionInnerContainer = optionContainer.querySelector(
      '.option-inner-container'
    );
    const optionIndex = Math.floor(
      (optionInnerContainer.scrollTop -
        (VISIBLE_ITEM_COUNT_BUFFER / 2) * OPTION_HEIGHT) /
        OPTION_HEIGHT
    );
    return optionIndex > 0 ? optionIndex : 0;
  }

  virtualizeOptions() {
    const start = this.calculateFirstVisibleOptionIndex();
    const limit = this.calculateVisibleItemCount();
    const end = Math.min(start + limit, this.filterOptions().length);
    return this.filterOptions().slice(start, end);
  }

  assignVisibleOptions() {
    if (this.virtualScroll) {
      this.visibleOptions = this.virtualizeOptions();
    }
    if (this.isLoading) {
      const optionContainer =
        this.shadowRoot.querySelector<HTMLElement>('.select-options');
      const optionInnerContainer = optionContainer.querySelector<HTMLElement>(
        '.option-inner-container'
      );
      console.log(
        optionInnerContainer.scrollTop + optionInnerContainer.offsetHeight
      );
      console.log('scrollHeight', optionInnerContainer.scrollHeight);
      if (
        optionInnerContainer.scrollTop + optionInnerContainer.offsetHeight >=
        optionInnerContainer.scrollHeight - OPTION_HEIGHT
      ) {
        this.dispatchCustomEvent('onLoadItems', {
          type: 'scroll',
        });
      }
    }
  }

  renderLoadingStateTop(options: string[] = []) {
    return !options && this.isLoading
      ? html`<kat-progress
          type="circular"
          size="small"
          indeterminate="true"
          tabindex="-1"
          part="loading-state"
          class="is-loading-state"
        ></kat-progress>`
      : nothing;
  }

  handleFocus(e) {
    const progress = e.target;
    const sibling = progress.previousElementSibling;
    if (sibling) {
      sibling.addEventListener('focus', e => {
        e.preventDefault();
        progress.scrollIntoView();
      });
      e.preventDefault();
      sibling.focus();
    }
  }

  renderLoadingStateBottom(options: string[] = []) {
    return options && this.isLoading
      ? html`<kat-progress
          type="circular"
          size="small"
          indeterminate="true"
          tabindex="-1"
          @focus=${this.handleFocus}
          part="loading-state"
          class="is-loading-state"
        ></kat-progress>`
      : nothing;
  }

  renderOption = (
    index: number,
    option: string | { name: string; value: string }
  ) => html`
    <div
      class="option"
      part="predictive-input-option-${index}"
      tabindex="-1"
      data-value="${option.name ? option.name : option}"
    >
      <div class="content">${option.name ? option.name : option}</div>
      <span class="ring"></span>
    </div>
  `;

  renderOptions() {
    const options = this.filterOptions();
    let topPadding = 0;
    let bottomPadding = 0;
    if (this.virtualScroll) {
      if (!this.visibleOptions.length) {
        this.visibleOptions = options.slice(
          0,
          this.calculateVisibleItemCount()
        );
      }
      topPadding = this.calculateFirstVisibleOptionIndex() * OPTION_HEIGHT;
      bottomPadding = Math.max(
        options.length * OPTION_HEIGHT -
          topPadding -
          this.visibleOptions.length * OPTION_HEIGHT,
        0
      );
    }
    return html`
      <div style="height: ${topPadding}px"></div>
      ${this.renderLoadingStateTop(options)}
      ${repeat(
        !this.virtualScroll ? options : this.visibleOptions,
        item => (typeof item === 'string' ? item : (item as any).name),
        (option, index) => this.renderOption(index, option)
      )}
      ${this.renderLoadingStateBottom(options)}
      <div style="height: ${bottomPadding}px"></div>
    `;
  }

  // Optionally renders a duplicate input for use when emulated modal is present
  // in order to prevent pulling the input out of page and into the modal.
  renderInput(isVisible = true) {
    let inputClass = 'header-row-text value ';
    if (this.state) {
      inputClass += this.state;
    }

    return isVisible
      ? html`
          <div class="select-header">
            <input
              class="${inputClass}"
              part="predictive-input-input"
              id="${this.uniqueId}"
              type="text"
              name="${ifNotNull(this.name)}"
              placeholder="${ifNotNull(this.placeholder)}"
              .value=${ifNotNull(this.value)}
              spellcheck=${this.katSpellcheck}
              maxlength="${ifNotNull(this.maxLength)}"
              autocomplete=${ifNotNull(this.autocomplete)}
              ?disabled="${this.disabled}"
              ?virtual-scroll="${this.virtualScroll}"
              ?readonly="${this.readOnly}"
              ?autofocus="${this.autofocus}"
              ?required="${this.required}"
              @focus=${this.inputFocus}
              @keydown=${this.inputKeyPress}
              @input=${this.onInput}
              aria-label=${ifNotNull(this.katAriaLabel)}
            />
            ${this.showClear && this.value
              ? html`
                  <button
                    class="close-icon"
                    part="close-button"
                    @click=${this._onSearchClear}
                  >
                    <kat-icon name="close" size="small"></kat-icon>
                  </button>
                `
              : ''}
            <span class="ring"></span>
          </div>
        `
      : nothing;
  }

  render() {
    const formLabel = this.label
      ? html`
          <kat-label
            class="form-label"
            part="predictive-input-label"
            for="${this.uniqueId}"
            text="${ifNotNull(this.label)}"
            .tooltipText=${ifNotNull(this.tooltipText)}
            .tooltipPosition=${ifNotNull(this.tooltipPosition)}
            .tooltipTriggerIcon=${ifNotNull(this.tooltipTriggerIcon)}
          ></kat-label>
        `
      : nothing;

    const constraintLabel = this.constraintLabel
      ? html`
          <kat-label
            class="constraint-label"
            part="predictive-input-constraint-label"
            for="${this.uniqueId}"
            variant="constraint"
            emphasis="${ifNotNull(this.constraintEmphasis)}"
            text="${this.constraintLabel}"
          ></kat-label>
        `
      : nothing;

    const stateLabel =
      this.stateLabel || this.stateEmphasis
        ? html`
            <kat-label
              class="state-label"
              part="predictive-input-state-label"
              variant="constraint"
              text="${ifNotNull(this.stateLabel)}"
              emphasis="${ifNotNull(this.stateEmphasis)}"
              state="${this.state}"
            ></kat-label>
          `
        : nothing;

    const containerClass = 'kat-select-container ' + this.size;

    return html`
      ${formLabel}
      <div class="${containerClass}" @change=${this.selectContainerChange}>
        ${this.renderInput()}
        <div part=${super.getPartMask(this.expanded)}>
          <div part=${super.getPartWrapper(this.expanded)}>
            <div part=${super.getPartContainer(this.expanded)}>
              ${this.renderInput(!!this.expanded && isMobile())}
              <div
                class="select-options"
                part=${super.getPartContent(
                  this.expanded,
                  'predictive-input-options'
                )}
                @click=${this.selectOptionClick}
                @keydown=${this.selectOptionKeydown}
              >
                <div
                  class="option-inner-container"
                  @scroll="${this.assignVisibleOptions}"
                  tabindex="-1"
                  style="${ifNotNull(
                    this.maxHeight &&
                      'max-height:' + this.maxHeight + ';overflow:auto;'
                  )}"
                >
                  ${cache(this.expanded ? this.renderOptions() : nothing)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="metadata">${constraintLabel} ${stateLabel}</div>
    `;
  }

  selectContainerChange(e) {
    e.stopPropagation();
    const target = e.target;
    this.value = e.detail ? e.detail.value : target.value;
    this.dispatchCustomEvent('change', { value: this.value });
  }

  selectOptionClick(event) {
    if (!this.disabled) {
      this.selectOption(event.target);
    }
  }

  selectOptionKeydown(evt) {
    const option = evt.target;
    if (this.disabled || !option) return;

    if (evt.keyCode === Keys.Space || evt.keyCode === Keys.Enter) {
      evt.preventDefault();
      this.selectOption(option);
    } else if (evt.keyCode === Keys.ArrowUp) {
      //previous
      evt.preventDefault();
      const prevEl = option.previousElementSibling;
      if (prevEl) {
        prevEl.focus();
      } else {
        const input = this.shadowRoot.querySelector('input');
        // this can never be null
        input.focus();
      }
    } else if (evt.keyCode === Keys.ArrowDown) {
      //next
      evt.preventDefault();
      const nextEl = option.nextElementSibling;
      nextEl?.focus();
    }
  }

  inputKeyPress(e) {
    const keyCode = e.keyCode || e.which;
    if (keyCode === Keys.ArrowDown && this.options.length > 0) {
      this.shadowRoot.querySelector<HTMLElement>('.option')?.focus();
    } else if (keyCode === Keys.Backspace && !this.expanded) {
      //backspace
      e.preventDefault();
      this.toggleExpanded(true);
    } else if (
      (keyCode === Keys.Enter || keyCode === Keys.Escape) &&
      this.expanded
    ) {
      this.toggleExpanded(false);
    }
  }

  @debounce(150)
  updateValue(value) {
    this.value = value;
  }

  onInput(evt) {
    // Stop event and re-emit with the correct value.  Events rely on the underlying values which are being debounced
    if (evt.stopPropagation) evt.stopPropagation();
    this.dispatchCustomEvent('input', { value: evt.target.value });
    this.updateValue(evt.target.value);
  }

  inputFocus(e) {
    const container = this.shadowRoot.querySelector('.kat-select-container');
    const newFocus = e.relatedTarget;
    if (!this.disabled && !container.contains(newFocus)) {
      this.toggleExpanded(true);
    }

    // Although 'focus' is not a custom event, it doesn't bubble up to parent elements.
    // Likewise, we will synthetically fire 'focus' and not have it bubble.
    this.dispatchCustomEvent('focus', { value: this.value }, false);
  }

  async toggleExpanded(newState) {
    if (this.expanded !== newState) {
      this.expanded = newState;
      await this.updateComplete;
      this.dispatchCustomEvent(
        this.expanded ? 'expand' : 'collapse',
        {},
        false
      );
    }
  }

  fitDropDownWindow() {
    const optionContainer =
      this.shadowRoot.querySelector<HTMLElement>('.select-options');
    const optionInnerContainer = optionContainer.querySelector<HTMLElement>(
      '.option-inner-container'
    );

    optionContainer.style.display = 'table';
    fitHandler(this, optionInnerContainer, true);
    optionContainer.style.removeProperty('display');
  }

  setOrUnsetOptionsContainerBottomOffset() {
    const selectOptionsElement =
      this.shadowRoot.querySelector('.select-options');

    if (!selectOptionsElement) {
      return;
    }

    this.fitDropDownWindow();

    if (this.hasAttribute('drop-up')) {
      const offset =
        this.shadowRoot.querySelector<HTMLElement>(
          'div.select-header'
        ).offsetHeight;

      selectOptionsElement.setAttribute('style', `bottom: ${offset}px`);
    } else {
      selectOptionsElement.removeAttribute('style');
    }
  }

  selectOption(element) {
    const intialValue = this.value;
    const newValue = element.closest('.option').dataset.value;
    if (intialValue !== newValue) {
      this.value = newValue;
      this.dispatchCustomEvent('change', { value: this.value });
    }
    this.toggleExpanded(false);
    this.shadowRoot.querySelector('input').focus();
  }

  filterOptions() {
    if (this._customOptionsFilter) {
      return this.options.filter(this._customOptionsFilter);
    }

    if (!this.value) {
      return this.options;
    }

    const filter = o => {
      const name = o.name || o;
      return this.filterType === 'startsWith'
        ? name.toLowerCase().startsWith(this.value.toLowerCase())
        : name.toLowerCase().indexOf(this.value.toLowerCase()) !== -1;
    };
    return this.options.filter(filter);
  }
}
